import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";
import { Copy, ArrowRight, Users, Coins, Share2, CheckCircle2, Gift, Zap, Trophy, Crown } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function BecomeOneOfUs() {
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hall, setHall] = useState([]);
  useSEO({
    title: "Become One of Us — Maqam Referrals",
    description: "Earn credits by inviting friends to Maqam. Saudi Arabia's AI job-application platform — built in the Kingdom.",
    path: "/become-one-of-us",
  });

  useEffect(() => {
    if (user) api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/referral/hall-of-fame").then((r) => setHall(r.data)).catch(() => {});
  }, [user]);

  const shareUrl = ref?.code
    ? `${window.location.origin}/register?ref=${encodeURIComponent(ref.code)}`
    : "";

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Join me on Maqam — apply to 100 Saudi jobs in one click. Use my code ${ref?.code} and get $5 in bonus CV sends: ${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const steps = [
    {
      icon: Share2,
      title: "01 — Share your link",
      body: "Copy your unique referral link and send it to friends, classmates, or colleagues looking for work.",
    },
    {
      icon: Users,
      title: "02 — They sign up",
      body: "When they create an account through your link, they instantly receive $5 worth of CV sends as a welcome.",
    },
    {
      icon: Coins,
      title: "03 — You earn $10",
      body: "You receive $10 of credit on your Maqam account — automatically deducted from your next monthly invoice.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-black">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F9FA] to-white" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-black/20 px-4 py-2 mb-8 font-mono text-xs uppercase tracking-widest">
              <Gift className="w-3.5 h-3.5 text-[#FF3B30]" />
              Referral program
            </div>
            <h1 className="font-display font-black text-5xl sm:text-7xl md:text-8xl tracking-tighter leading-[0.9] mb-8">
              Become one <br /> of <span className="text-gradient">us.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-2xl leading-relaxed mb-6">
              Invite friends. <span className="font-bold text-black">They get $5</span> in bonus CV sends.{" "}
              <span className="font-bold text-[#0033FF]">You get $10</span> deducted from your next monthly bill. Every
              time. No cap.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              {user ? (
                <>
                  <button
                    onClick={copyLink}
                    className="bg-black text-white hover:bg-[#0033FF] font-bold text-base px-8 py-5 flex items-center gap-3 transition-colors"
                    data-testid="share-copy"
                  >
                    {copied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    {copied ? "Link copied" : "Copy my link"}
                  </button>
                  <button
                    onClick={shareWhatsApp}
                    className="border border-black hover:bg-black hover:text-white font-bold px-8 py-5 flex items-center gap-3 transition-colors"
                    data-testid="share-whatsapp"
                  >
                    <Share2 className="w-5 h-5" /> Share on WhatsApp
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="bg-black text-white hover:bg-[#0033FF] font-bold text-base px-8 py-5 flex items-center gap-3 transition-colors"
                    data-testid="join-now"
                  >
                    Create account to start <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/login" className="font-bold px-5 py-5 link-underline">
                    I already have an account
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Earnings card */}
          <div className="md:col-span-5 bg-black text-white p-8 md:p-10 relative">
            <Zap className="w-8 h-8 text-[#FF3B30] mb-6" />
            <div className="font-mono text-xs uppercase tracking-widest text-gray-400 mb-3">Example earnings</div>
            <div className="space-y-4">
              {[
                { friends: 5, you: 15, note: "Covers a third of your Basic plan next month" },
                { friends: 10, you: 30, note: "Nearly half off your Basic plan" },
                { friends: 20, you: 50, note: "Basic plan discounted more than half" },
                { friends: 50, you: 100, note: "You're now a Maqam ambassador" },
              ].map((row) => (
                <div key={row.friends} className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-3">
                  <div>
                    <div className="font-display font-black text-3xl">{row.friends} <span className="text-sm font-medium text-gray-400">friends</span></div>
                    <div className="text-xs text-gray-400 mt-1">{row.note}</div>
                  </div>
                  <div className="text-end">
                    <div className="font-mono text-xs text-gray-400">you earn</div>
                    <div className="font-display font-black text-2xl text-[#34C759]">
                      ${row.you}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">/ How the program works</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-16 max-w-3xl">
            Three steps. No paperwork. Automatic.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {steps.map((s) => (
              <div key={s.title} className="bg-white p-10">
                <s.icon className="w-8 h-8 mb-8 stroke-[1.5]" />
                <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{s.title}</h3>
                <p className="text-gray-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active referral code panel (logged-in) */}
      {user && ref && (
        <section className="border-b border-black/10 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-black/10 bg-white p-8">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Your code</div>
                <div className="flex items-center gap-3">
                  <span className="font-display font-black text-3xl tracking-tighter" data-testid="my-code">{ref.code}</span>
                  <button onClick={copyLink} className="p-2 border border-black/20 hover:bg-black hover:text-white" data-testid="copy-link-panel">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 text-xs font-mono text-gray-500 break-all">{shareUrl}</div>
              </div>
              <div className="border border-black/10 bg-white p-8">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Friends invited</div>
                <div className="num-ticker font-display font-black text-5xl tracking-tighter">{ref.invited_count}</div>
              </div>
              <div className="border border-black/10 bg-black text-white p-8">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Your credit</div>
                <div className="num-ticker font-display font-black text-5xl tracking-tighter text-[#34C759]">
                  ${ref.credit_usd}
                </div>
                <div className="text-xs text-gray-400 mt-2">Auto-deducted from your next bill</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hall of Fame */}
      <section className="border-b border-black/10 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF3B30] mb-4 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" />
                Hall of Fame
              </div>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                Top referrers<br />
                <span className="text-[#FF3B30]">of all time.</span>
              </h2>
            </div>
            <div className="text-sm text-gray-400 max-w-sm">
              Our most generous ambassadors — ranked by number of friends invited. Names are lightly masked for privacy.
            </div>
          </div>
          {hall.length === 0 ? (
            <div className="border border-dashed border-white/20 p-12 text-center text-gray-500">
              No referrals yet. Be the first name on the board — share your link above.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="hall-of-fame">
              {hall.map((row, i) => {
                const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                return (
                  <div key={i} className="bg-black p-5 flex items-center gap-5" data-testid={`hof-${i}`}>
                    <div className={`num-ticker font-display font-black text-4xl tracking-tighter ${i < 3 ? "text-[#FF3B30]" : "text-gray-400"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-lg truncate">
                        {medal && <span className="me-2">{medal}</span>}
                        {row.name}
                      </div>
                      <div className="font-mono text-xs text-gray-400 uppercase tracking-widest">
                        {row.invited} friends invited
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">earned</div>
                      <div className="num-ticker font-display font-black text-2xl text-[#34C759]">
                        ${row.credit_earned_usd}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* FAQ-style */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">/ FAQ</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                Small print, straight answers.
              </h2>
            </div>
            <div className="space-y-6">
              {[
                { q: "When do I actually get my $10?", a: "The moment a friend signs up with your link. It's added to your credit balance and automatically applied to your next monthly invoice." },
                { q: "Is there a cap?", a: "Not at the moment — invite as many people as you want. Every valid signup credits your account." },
                { q: "Can I cash out the credit?", a: "The credit is non-cash and only reducing subscription fees. Once you've covered an entire month for free, overflow carries to the following month." },
                { q: "What if my friend never subscribes?", a: "Doesn't matter. Both rewards trigger on signup, not on payment. They still get their $5 in bonus CV sends even on the free plan." },
              ].map((f) => (
                <div key={f.q} className="border-b border-black/10 pb-5">
                  <div className="font-display font-bold mb-2">{f.q}</div>
                  <div className="text-gray-700 text-sm leading-relaxed">{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-sm text-gray-500 flex justify-between flex-wrap gap-3">
          <div>© 2026 Maqam. Built for the Kingdom.</div>
          <div className="flex gap-6">
            <Link to="/news" className="link-underline">News</Link>
            <Link to="/tips" className="link-underline">Tips</Link>
            <Link to="/#pricing" className="link-underline">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
